import React, { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../../assets/ts/_utils'
import { useThemeMode } from '../../../layout/theme-mode/ThemeModeProvider'
import { useIntl } from 'react-intl'

type Props = {
  className: string
  title?: string
  totalLabel?: string
  operatorData?: OperatorVerdictStat[]
  totalCount?: number
  scanPending?: number
  scanFailed?: number
  showPending?: boolean
}

type OperatorVerdictStat = {
  operator: string
  whitelisted: number
  blacklisted: number
  pending?: number
}

const dummyOperatorData: OperatorVerdictStat[] = [
  { operator: 'GOFLIPO', whitelisted: 6, blacklisted: 2, pending: 1 },
  { operator: 'Airtel',  whitelisted: 5, blacklisted: 2, pending: 2 },
  { operator: 'Vi',      whitelisted: 4, blacklisted: 2, pending: 3 },
  { operator: 'Jio',     whitelisted: 7, blacklisted: 1, pending: 1 },
  { operator: 'BSNL',    whitelisted: 4, blacklisted: 1, pending: 4 },
  { operator: 'Tata',    whitelisted: 6, blacklisted: 2, pending: 1 },
  { operator: 'Smartping', whitelisted: 5, blacklisted: 1, pending: 3 },
]

const ChartsWidgetVerdictBifurcation: React.FC<Props> = ({
  className,
  title,
  totalLabel,
  operatorData,
  totalCount,
  scanPending = 0,
  scanFailed = 0,
  showPending = true,
}) => {
  const intl = useIntl()
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const resolvedTitle = title ?? intl.formatMessage({ id: 'DASHBOARD.CHART.IP_REGISTRATION_SUMMARY' })
  const resolvedTotalLabel = totalLabel ?? intl.formatMessage({ id: 'DASHBOARD.CHART.IPS_REGISTERED_LABEL' })
  const labelWhitelisted = intl.formatMessage({ id: 'COMMON.STATUS.WHITELISTED' })
  const labelBlacklisted = intl.formatMessage({ id: 'COMMON.STATUS.BLACKLISTED' })
  const labelPending = intl.formatMessage({ id: 'COMMON.STATUS.PENDING' })

  const chartData = operatorData && operatorData.length > 0 ? operatorData : dummyOperatorData
  const broadcastOperatorCount = chartData.filter((item) => item.operator !== 'GOFLIPO').length
  const maxValue = Math.max(
    1,
    ...chartData.flatMap((item) => [
      item.whitelisted,
      item.blacklisted,
      ...(showPending ? [item.pending ?? 0] : []),
    ])
  )

  const refreshChart = () => {
    if (!chartRef.current) return
    const height = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, chartData, maxValue, showPending, labelWhitelisted, labelBlacklisted, labelPending)
    )
    if (chart) chart.render()
    return chart
  }

  useEffect(() => {
    const chart = refreshChart()
    return () => {
      if (chart) chart.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, mode, operatorData])

  return (
    <div className={`card card-flush ${className}`}>
      <div className='card-header pt-5 border-0'>
        <div className='card-title d-flex flex-column'>
          {resolvedTitle ? <span className='card-label fw-bold fs-4 mb-3'>{resolvedTitle}</span> : null}
          <div className='d-flex'>
            <div className='d-flex align-items-center'>
              <span className='fs-2hx fw-bold text-dark me-2 lh-1 ls-n2'>
                {totalCount ?? chartData.length}
              </span>
            </div>
            <div className='d-flex flex-column'>
              <span className='pt-1 fw-semibold fs-6'>{resolvedTotalLabel}</span>
              <span className='text-gray-500 fw-semibold fs-8'>
                {intl.formatMessage({ id: 'DASHBOARD.CHART.BROADCAST_VERDICT' }, { broadcastOperatorCount })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className='card-body pt-0 pb-4'>
        <div ref={chartRef} style={{ height: '320px' }} />
      </div>
    </div>
  )
}

function getChartOptions(
  height: number,
  chartData: OperatorVerdictStat[],
  maxValue: number,
  showPending = true,
  labelWhitelisted = 'Whitelisted',
  labelBlacklisted = 'Blacklisted',
  labelPending = 'Pending'
): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-700')
  const borderColor = getCSSVariableValue('--bs-gray-200')

  const series = [
    { name: labelWhitelisted, data: chartData.map((item) => item.whitelisted) },
    { name: labelBlacklisted, data: chartData.map((item) => item.blacklisted) },
    ...(showPending
      ? [{ name: labelPending, data: chartData.map((item) => item.pending ?? 0) }]
      : []),
  ]

  const colors = [
    getCSSVariableValue('--bs-success'),
    getCSSVariableValue('--bs-danger'),
    ...(showPending ? [getCSSVariableValue('--bs-warning')] : []),
  ]

  return {
    series,
    colors,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 2,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '10px',
        fontWeight: '600',
        colors: [labelColor],
      },
      offsetY: -20,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: chartData.map((item) => item.operator),
      labels: {
        style: { colors: labelColor, fontSize: '12px', fontWeight: 500 },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxValue * 1.05),
      tickAmount: Math.min(6, Math.max(2, maxValue)),
      labels: { show: false },
    },
    fill: { opacity: 1 },
    grid: {
      borderColor,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      labels: { colors: labelColor },
      markers: { radius: 12 },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      custom: function ({ dataPointIndex }: any) {
        const item = chartData[dataPointIndex]
        return `
          <div style="background:#fff;padding:12px 16px;border-radius:4px;box-shadow:0 1px 3px rgba(0,0,0,0.1);border:1px solid #e0e0e0;">
            <strong style="display:block;margin-bottom:8px;color:#000;">${item?.operator}</strong>
            <div style="font-size:12px;line-height:1.6;color:#000;">
              <div>● ${labelWhitelisted}: ${item?.whitelisted}</div>
              <div>● ${labelBlacklisted}: ${item?.blacklisted}</div>
              ${showPending ? `<div>● ${labelPending}: ${item?.pending ?? 0}</div>` : ''}
            </div>
          </div>
        `
      },
    },
  }
}

export { ChartsWidgetVerdictBifurcation }
